import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Form, Input, message } from 'antd'
import { AUTH } from '../../utils/apis'
import paths from '../../Config/paths'
import { secureApi } from '../../Config/api'
import { Lock, ArrowRight, BarChart3, Shield, Eye, EyeOff } from 'lucide-react'
import '../Login/LoginPage.css'

const ResetPassword = () => {
    const { token } = useParams()
    console.log('Reset token:', token)
    // Get token from the URL params
    const [loading, setLoading] = useState(false)
    const history = useHistory()

    const [passwordVisible, setPasswordVisible] = useState(false)
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible)
    }

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible)
    }

    const onFinish = async (values) => {
        try {
            setLoading(true)

            // Make the API request to reset the password
            const response = await secureApi.post(`${AUTH?.RESET_PASSWORD}/${token}`, {
                newPassword: values?.password
            })

            console.log('Reset response:', response)
            if (response?.success) {
                message.success(response?.message)
                history.push(paths?.LOGIN)
            } else {
                message.error(response?.message || 'Reset failed')
            }
        } catch (err) {
            message.error(err.response?.data?.message || 'Something went wrong')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="login-container">
            <div className="left-section">
                <div className="left-content">
                    <div className="logo-section">
                        <div className="logo-icon">
                            <span className="logo-letter">E</span>
                        </div>
                        <div className="logo-text">
                            <div className="company-name">ELMEC</div>
                            <div className="company-subtitle">ELETTRONICA</div>
                        </div>
                    </div>

                    <div className="welcome-section">
                        <h1 className="main-heading">
                            Reset your <br />
                            <span className="dashboard-text">Password</span>
                        </h1>
                        <p className="description">
                            Enter your new password below and confirm it <br />
                            to regain access to your QC dashboard.
                        </p>
                    </div>

                    <div className="features-section">
                        <div className="feature-card">
                            <div className="feature-icon analytics-icon">
                                <BarChart3 size={24} />
                            </div>
                            <div className="feature-content">
                                <h3 className="feature-title">Analytics</h3>
                                <p className="feature-description">Real-time insights and reporting</p>
                            </div>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon security-icon">
                                <Shield size={24} />
                            </div>
                            <div className="feature-content">
                                <h3 className="feature-title">Security</h3>
                                <p className="feature-description">Enterprise-grade protection</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section */}
            <div className="right-section">
                <div className="login-form-container">
                    <div className="form-header">
                        <h2 className="form-title">Reset Password</h2>
                        <p className="form-subtitle">
                            Create a new password for your account
                        </p>
                    </div>

                    <Form onFinish={onFinish} layout="vertical" className="login-form">
                        {/* New Password */}
                        <Form.Item
                            name="password"
                            label="New Password"
                            rules={[{ required: true, message: 'Please input your new password!' }, { min: 6, message: 'Password must be at least 6 characters!' }]}
                            className="input-group"
                        >
                            <div className="input-wrapper">
                                <Lock className="input-icon" size={20} />
                                <Input
                                    type={passwordVisible ? 'text' : 'password'}
                                    placeholder="New Password"
                                    className="form-input"
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="password-toggle"
                                >
                                    {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </Form.Item>

                        {/* Confirm Password */}
                        <Form.Item
                            name="confirmPassword"
                            label="Confirm Password"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: 'Please confirm your password!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve()
                                        }
                                        return Promise.reject('Passwords do not match!')
                                    }
                                })
                            ]}
                            className="input-group"
                        >
                            <div className="input-wrapper">
                                <Lock className="input-icon" size={20} />
                                <Input
                                    type={confirmPasswordVisible ? 'text' : 'password'}
                                    placeholder="Confirm Password"
                                    className="form-input"
                                />
                                <button
                                    type="button"
                                    onClick={toggleConfirmPasswordVisibility}
                                    className="password-toggle"
                                >
                                    {confirmPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </Form.Item>

                        <Form.Item>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`submit-button ${loading ? 'loading' : ''}`}
                            >
                                {loading ? 'Resetting...' : (
                                    <>
                                        Reset Password
                                        <ArrowRight size={20} />
                                    </>
                                )}
                            </button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    )
}

export default ResetPassword
